import axios from 'axios';
import { API_BASE_URL } from '../utils/config';

export interface CoachAvailabilityDto {
  availabilityId: string;
  coachId: string;
  coachName: string;
  availabilityDate: string;
  startTime: string;
  endTime: string;
  notes?: string;
  isActive: boolean;
  /** Heti sorozat közös azonosítója (null = egyedi elérhetőség). */
  seriesId?: string | null;
  venues: VenueDto[];
  createdDate: string;
  modifiedDate: string;
}

/** Heti ismétléssel létrehozott sorozat kérése — az egyedi mezők + meddig és hány hetente. */
export interface CreateCoachAvailabilitySeriesDto extends CreateCoachAvailabilityDto {
  /** Az utolsó nap (bezárólag), ameddig az alkalmak létrejönnek — yyyy-MM-dd. */
  repeatUntil: string;
  /** 1 = hetente, 2 = kéthetente … (1–4). */
  intervalWeeks: number;
}

export interface CoachAvailabilitySeriesResultDto {
  seriesId: string;
  created: CoachAvailabilityDto[];
  skipped: { date: string; reason: string }[];
}

export interface CreateCoachAvailabilityDto {
  coachId?: string; // Optional: Admins can specify coach ID to create for another coach
  availabilityDate: string;
  startTime: string;
  endTime: string;
  notes?: string;
  venueIds: string[];
}

export interface UpdateCoachAvailabilityDto {
  availabilityDate: string;
  startTime: string;
  endTime: string;
  notes?: string;
  venueIds: string[];
  isActive: boolean;
}

export interface CalendarCoachAvailabilityDto {
  availabilityId: string;
  seriesId?: string | null;
  coachId: string;
  coachName: string;
  availabilityDate: string;
  startTime: string;
  endTime: string;
  notes?: string;
  venueNames: string[];
  venueIds: string[];
}

export interface CoachAvailabilityQueryDto {
  startDate: string;
  endDate: string;
  coachId?: string;
  venueId?: string;
}

export interface VenueDto {
  venueId: string;
  name: string;
  tableCount: number;
  isActive: boolean;
}

class CoachAvailabilityService {
  private getAuthHeader() {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async getCoachAvailabilities(params?: {
    startDate?: string;
    endDate?: string;
    coachId?: string;
    venueId?: string;
  }): Promise<CoachAvailabilityDto[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/coach-availability`, {
        headers: this.getAuthHeader(),
        params
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching coach availabilities:', error);
      throw error;
    }
  }

  async getCalendarAvailabilities(query: CoachAvailabilityQueryDto): Promise<CalendarCoachAvailabilityDto[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/coach-availability/calendar`, {
        headers: this.getAuthHeader(),
        params: query
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching calendar availabilities:', error);
      throw error;
    }
  }

  async getCoachAvailability(id: string): Promise<CoachAvailabilityDto> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/coach-availability/${id}`, {
        headers: this.getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching coach availability:', error);
      throw error;
    }
  }

  async createCoachAvailability(availability: CreateCoachAvailabilityDto): Promise<CoachAvailabilityDto> {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/coach-availability`, availability, {
        headers: this.getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error creating coach availability:', error);
      throw error;
    }
  }

  /**
   * Heti ismétléssel létrehozott sorozat: egy mentés, több alkalom. Az ütköző napokat a szerver
   * kihagyja és a `skipped` listában jelenti (részleges siker). [UX 2026-09-12]
   */
  async createCoachAvailabilitySeries(series: CreateCoachAvailabilitySeriesDto): Promise<CoachAvailabilitySeriesResultDto> {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/coach-availability/series`, series, {
        headers: this.getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error creating coach availability series:', error);
      throw error;
    }
  }

  /** A sorozat tagjainak törlése egy naptól kezdve (alapból a mai naptól); a korábbi napok maradnak. */
  async deleteCoachAvailabilitySeries(seriesId: string, fromDate?: string): Promise<{ deleted: number }> {
    try {
      const response = await axios.delete(`${API_BASE_URL}/api/coach-availability/series/${seriesId}`, {
        headers: this.getAuthHeader(),
        params: fromDate ? { fromDate } : undefined
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting coach availability series:', error);
      throw error;
    }
  }

  async updateCoachAvailability(id: string, availability: UpdateCoachAvailabilityDto): Promise<CoachAvailabilityDto> {
    try {
      const response = await axios.put(`${API_BASE_URL}/api/coach-availability/${id}`, availability, {
        headers: this.getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error updating coach availability:', error);
      throw error;
    }
  }

  async deleteCoachAvailability(id: string): Promise<void> {
    try {
      await axios.delete(`${API_BASE_URL}/api/coach-availability/${id}`, {
        headers: this.getAuthHeader()
      });
    } catch (error) {
      console.error('Error deleting coach availability:', error);
      throw error;
    }
  }

  // Helper method to get availability for a specific month
  async getMonthAvailabilities(year: number, month: number, coachId?: string, venueId?: string): Promise<CalendarCoachAvailabilityDto[]> {
    // Calculate the full calendar view range (6 weeks = 42 days)
    const firstDayOfMonth = new Date(year, month - 1, 1);
    const firstDayWeekday = (firstDayOfMonth.getDay() || 7) - 1; // Monday = 0

    // Start from the first day shown in calendar (may be from previous month)
    const calendarStartDate = new Date(year, month - 1, 1 - firstDayWeekday);

    // End at the last day shown in calendar (42 days total for 6 weeks)
    const calendarEndDate = new Date(calendarStartDate);
    calendarEndDate.setDate(calendarStartDate.getDate() + 41); // 42 days total (0-41 = 42 days)

    const startDate = calendarStartDate.toISOString().split('T')[0];
    const endDate = calendarEndDate.toISOString().split('T')[0];

    return this.getCalendarAvailabilities({
      startDate,
      endDate,
      coachId,
      venueId
    });
  }

  // Helper method to get availability for a specific day
  async getDayAvailabilities(date: string, coachId?: string, venueId?: string): Promise<CalendarCoachAvailabilityDto[]> {
    return this.getCalendarAvailabilities({
      startDate: date,
      endDate: date,
      coachId,
      venueId
    });
  }

  // Helper method to get availability for a specific date range
  async getDateRangeAvailabilities(
    startDate: string,
    endDate: string,
    coachId?: string,
    venueId?: string
  ): Promise<CalendarCoachAvailabilityDto[]> {
    return this.getCalendarAvailabilities({
      startDate,
      endDate,
      coachId,
      venueId
    });
  }
}

export default new CoachAvailabilityService();